import React, { useState, useEffect, createContext, ReactNode } from "react";
import axios from "axios";
import { API_URL } from "../api/config";
import { toast } from "react-toastify";
import { removeFCMToken } from "Helper/removeFCMToken";
import { useLocation } from "@reach/router";

interface OwnerData {
  _id: string;
  firstname: string;
  lastname: string;
  email: string;
  phoneNumber: string;
  image: string;
  direcCoins_wallet: Number;
}
interface PropertyData {
  _id: any;
  title: string;
  // address: string;
  Address: string;
  price: string;
}
interface Booking {
  checkinDate: string;
  checkoutDate: string;
}

interface AuthContextProps {
  ownerData: OwnerData;
  onLogout: () => void;
  getAdminData: () => Promise<void>;
  getOwnerProperty: () => Promise<void>;
  getOnePropertyDetails: any;
  fetchBookings: any;
  checkinDates: any;
  checkoutDates: any;
  bookedDates: any;
  topUps:any;
  setTopUps:any;
  getOwnerTopUps:any;
  getTransactionDetails:any;
  transactions:any;
  setTransactions:any;

}

const initialState: AuthContextProps = {
  ownerData: {
    _id: "",
    firstname: "",
    lastname: "",
    email: "",
    phoneNumber: "",
    image: "",
    direcCoins_wallet: 0,
  },
  onLogout: () => {},
  getAdminData: async () => {},
  getOwnerProperty: async () => {},
  getOnePropertyDetails: async () => {},
  fetchBookings: async () => {},
  checkinDates: [],
  checkoutDates: [],
  bookedDates: [],
  topUps:[],
  setTopUps:() => {},
  getOwnerTopUps:() => {},
  getTransactionDetails: () => {},
  transactions:[],
  setTransactions:() => {},

};

const AuthContext = createContext<AuthContextProps>(initialState);

interface AuthProviderProps {
  children: ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [ownerData, setOwnerData] = useState<OwnerData>({
    _id: "",
    firstname: "",
    lastname: "",
    email: "",
    dateOfBirth: "",
    phoneNumber: "",
    image: "",
    direcCoins_wallet: "",
  } as any);
  const [propertyData, setPropertyData] = useState([]);

  const [bookedDates, setBookedDates] = useState<Date[]>([]);
  const [checkinDates, setCheckinDates] = useState<Date[]>([]);
  const [checkoutDates, setCheckoutDates] = useState<Date[]>([]);
  const [loading, setLoading] = useState(false);
  const [topUps, setTopUps] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const token = localStorage.getItem('token');

  const onLogout = async () => {
    await removeFCMToken();
    setOwnerData(initialState.ownerData);

    // List of keys to preserve
    const keysToPreserve = ["isPdfGenerated"];

    // Store preserved values temporarily
    const preservedValues: Record<string, string> = {};
    keysToPreserve.forEach((key: any) => {
      const value = localStorage.getItem(key);
      if (value !== null) {
        preservedValues[key] = value;
      }
    });

    localStorage.clear();
    Object.entries(preservedValues).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });

    sessionStorage.clear();
    window.location.href = "/login";

  };

  const getAdminData = async () => {
    const token = localStorage.getItem("token");

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/owner/get-owner`, {
        headers: {
          token: token,
        },
      });
      if (response.data.error === false) {
        const info = response.data.ownerdata;
        setOwnerData({
          _id: info._id,
          firstname: info.firstname,
          lastname: info.lastname,
          email: info.email,
          phoneNumber: info.phoneNumber,
          image: info.image,
          direcCoins_wallet: info.direcCoins_wallet,
        });
      } else if (response.data.error === true) {
        toast.error(response.data.message);
        onLogout();
      }
      setLoading(false);
    } catch (err) {
      console.error("error while fetching userInfo", err);
      toast.error(`Invalid or expired token! Please log in again.`);
      //if path is not login then logout
      const currentPathname = window?.location?.pathname.substring(1) 
      if(currentPathname != "login"){
        onLogout();
      }
      setLoading(false);
    }
  };

  const getOwnerProperty = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${API_URL}/property/get-owner-property`,
        {
          headers: {
            token: token,
          },
        }
      );
      if (response.data.error === false) {
        const property = response.data.propertydata;

        setPropertyData(property);
      }
      // Set loading to false when data is successfully loaded
    } catch (err) {
      console.error("error while fetching properties data", err);
    }
  };

  const getOnePropertyDetails = async (propertyId: any) => {
    if (!propertyId) {
      console.error("Property ID is null or undefined");
      return;
    }
    try {
      const response = await axios.get(
        `${API_URL}/property/get-propertyCard-details?id=${propertyId}`
      );
      if (response.data.error === false) {
        if (response.data.getTransaction) {
        }
        setPropertyData(response.data.propertyDetails);
      }
    } catch (err) {
      toast.error("Error while fetching property details");
      console.error("Error fetching details", err);
    }
  };

  // Function to generate all dates between check-in and check-out
  const getDatesBetween = (
    checkinDate: string | number | Date,
    checkoutDate: string | number | Date
  ) => {
    let dates = [];
    let currentDate = new Date(checkinDate);
    let endDate = new Date(checkoutDate);
    while (currentDate < endDate) {
      dates.push(new Date(currentDate)); // Push as Date object
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return dates;
  };

  // get property bookings
  const fetchBookings = async (propIdParam: any) => {
    try {
      const response = await axios.get(
        `${API_URL}/property/property-bookings?propID=${propIdParam}`
      );
      const bookingsArray: Booking[] = Array.isArray(response.data.bookings)
        ? response.data.bookings
        : [];

      let allBookedDates: Date[] = [];
      let checkins: Date[] = [];
      let checkouts: Date[] = [];

      bookingsArray.forEach((booking) => {
        const { checkinDate, checkoutDate } = booking;
        const datesInRange = getDatesBetween(checkinDate, checkoutDate);
        allBookedDates.push(...datesInRange);
        checkins.push(new Date(checkinDate));
        checkouts.push(new Date(checkoutDate));
      });

      setBookedDates(allBookedDates);
      setCheckinDates(checkins);
      setCheckoutDates(checkouts);
    } catch (error) {
      console.error("Error fetching bookings:", error);
    }
  };
 const getOwnerTopUps = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(`${API_URL}/owner/get-topUps`, {
        headers:{
          token:token
        }
      });
      if(response.data.error === false) {
        setTopUps(response.data.topUps);
      } else if(response.data.error === true) {
        toast.error("Error fetching TopUps");
      }
    } catch (error) {
      console.log("Error fetching topUps", error);
      
    }
  }

   const getTransactionDetails = async () => {
     const token = localStorage.getItem("token");
     try {
       if (!ownerData._id) {
         console.error("Owner Id is null or undefined");
         return;
       }
       const response = await axios.get(
         `${API_URL}/transaction/owner-transactions`,
         {
           headers: {
             token: token,
           },
         }
       );
       if (response.data.error === false) {
         setTransactions(response.data.transactions);
       }
     } catch (error) {
       toast.error("Error while fetching transaction details");
       console.log("Error while fetching transaction details", error);
     }
   };

  const pathnameArray = ["login", "signup", "verify", "verifyotp"];

  useEffect(() => {
    const hasToken = !!localStorage.getItem("token");
    const currentPathname = window?.location?.pathname.substring(1);

    if (!pathnameArray.includes(currentPathname) && !hasToken) {
      onLogout();
    }
    if (!pathnameArray.includes(currentPathname) && hasToken) {
      getAdminData();
    }
  }, [window?.location]);

  useEffect(() => {
    getAdminData()
  }, [token])
  
    useEffect(() => {
    getOwnerTopUps();
  }, [ownerData?._id])

  const value: AuthContextProps = {
    ownerData,
    onLogout,
    getAdminData,
    getOwnerProperty,
    getOnePropertyDetails,
    fetchBookings,
    checkinDates,
    checkoutDates,
    bookedDates,
    topUps,
    setTopUps,
    getOwnerTopUps,
    getTransactionDetails,
    transactions,
    setTransactions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const AuthConsumer = AuthContext.Consumer;

export { AuthProvider, AuthConsumer, AuthContext };
  function setTransactions(transactions: any) {
    throw new Error("Function not implemented.");
  }

